import TrackingService from '@/services/TrackingService'
import { resolveZendeskScript } from '@/templates/config'
import { ActionType, SourceObjectValueElementSubtype, EventSubtypes, SourceObjectKey } from '@/types/events'

const ZENDESK_SETUP_RETRY_TIMEOUT_MS = 100

enum ZendeskEvents {
  ConversationOpened = 'conversationOpened',
  Close = 'close',
}

type EventCallbacksMap = { [key in ZendeskEvents]: (() => void)[] }

class ZendeskService {
  isInitialized = false
  private callbacksRegistered = false
  private pendingOpenChat = false
  private eventCallbacksMap: EventCallbacksMap = {
    [ZendeskEvents.ConversationOpened]: [],
    [ZendeskEvents.Close]: [],
  }

  init(scriptSrc = resolveZendeskScript(import.meta.env.VITE_TEMPLATE_ID, import.meta.env.PROD)): void {
    if (document.getElementById('ze-snippet')) {
      this.setup()

      return
    }

    const script = document.createElement('script')

    script.setAttribute('id', 'ze-snippet')
    script.setAttribute('src', scriptSrc)
    script.onload = this.setup.bind(this)
    document.head.appendChild(script)
  }

  setup(): void {
    if (!this.isZendeskWidgetAvailable) {
      setTimeout(() => this.setup(), ZENDESK_SETUP_RETRY_TIMEOUT_MS)

      return
    }

    this.isInitialized = true

    this.setupWebWidget()
    this.setupChatEventCallbacks()

    if (this.pendingOpenChat) {
      this.openChat()
      this.pendingOpenChat = false
    }
  }

  private setupWebWidget(): void {
    window.zE('messenger:set', 'customization', {
      theme: {
        primary: '#204D40',
      },
    })

    window.zE('messenger:set', 'locale', 'en')

    window.zE('messenger:set', 'conversationTags', ['new_psp'])
  }

  private get isZendeskWidgetAvailable(): boolean {
    return typeof window.zE === 'function'
  }

  private setupChatEventCallbacks(): void {
    if (this.callbacksRegistered || !this.isZendeskWidgetAvailable) {
      return
    }

    try {
      const events = Object.values(ZendeskEvents)

      events.forEach((event) => {
        window.zE('messenger:on', event, () => {
          const callbacks = this.eventCallbacksMap[event]

          callbacks.forEach((callback) => callback())
        })
      })

      this.addConversationOpenedCallback(() => {
        TrackingService.trackComponentInteraction(
          ActionType.ChatStart,
          [
            {
              key: SourceObjectKey.ElementSubtype,
              value: SourceObjectValueElementSubtype.SupportPopup,
            },
          ],
          EventSubtypes.DescriptorPage,
        )
      })

      this.addCloseCallback(() => {
        TrackingService.trackComponentInteraction(
          ActionType.ChatEnd,
          [
            {
              key: SourceObjectKey.ElementSubtype,
              value: SourceObjectValueElementSubtype.SupportPopup,
            },
          ],
          EventSubtypes.DescriptorPage,
        )
      })

      this.callbacksRegistered = true
    } catch (e) {
      // Error handling left blank as per existing pattern in TrackingService
    }
  }

  private addConversationOpenedCallback(callback: () => void): void {
    this.eventCallbacksMap[ZendeskEvents.ConversationOpened].push(callback)
  }

  private addCloseCallback(callback: () => void): void {
    this.eventCallbacksMap[ZendeskEvents.Close].push(callback)
  }

  openChat(): void {
    if (!this.isInitialized || !this.isZendeskWidgetAvailable) {
      this.pendingOpenChat = true

      return
    }
    window.zE('messenger', 'open')
  }
}

export default new ZendeskService()
